import adwordsImg from '@/assets/images/integrations/adwords.svg';
import facebookImg from '@/assets/images/integrations/facebook.svg';
import typeformImg from '@/assets/images/integrations/typeform.svg';
import jotformImg from '@/assets/images/integrations/jotform.svg';
import segmentOutImg from '@/assets/images/integrations/segment.svg';
import lineImg from '@/assets/images/integrations/line.svg';
import twilioImg from '@/assets/images/integrations/twilio.svg';
import cdpOutImg from '@/assets/images/logos/logomark-journeys.svg';
import { DataFlow, IntegrationCategory, IntegrationType } from './enums';
import { IntegrationIDs, IntegrationNames } from './ids';
import type { StaticIntegration } from './types';

/**
 * Taxonomy terms the base catalog descriptions embed. Callers pass the resolved
 * terms (via `useProductTaxonomy().term(...)`); the defaults are the untranslated
 * nouns (`'customers'` / `'customer'`).
 */
export type StaticIntegrationTerms = {
  customersPlural?: string;
  customerSingular?: string;
};

/**
 * The base catalog records the synthesized list spreads and augments. Only 13
 * entries — the rest of the catalog is synthesized dynamically from the backend
 * list plus fan-out status (see `directory/synthesize.ts`).
 *
 * Slack / mParticle image URLs point at the Brandfetch lettermark CDN with no
 * `?c=<client-id>` query — Hydra has no brandfetch token surface (see PR notes).
 */
export function getStaticIntegrations(terms: StaticIntegrationTerms = {}): Record<string, StaticIntegration> {
  const customersPlural = terms.customersPlural ?? 'customers';
  const customerSingular = terms.customerSingular ?? 'customer';

  return Object.freeze<Record<string, StaticIntegration>>({
    adwords: {
      id: IntegrationIDs.adwords,
      forceOrder: 0,
      imageSrc: adwordsImg,
      name: IntegrationNames.adwords,
      overview: 'Sync segments to Google Ad Audiences for retargeting on Google.',
      category: IntegrationCategory.advertising,
      description: 'Google Ads connect your message to the right audience',
      parsedUseCases: [
        'Sync Customer.io segments to Google Ad Audiences',
        'Create Google Similar Audiences based on these Ad Audiences',
      ],
      enabled: false,
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      audienceSync: true,
      path: 'integrations-public.type',
      dynamicSegment: 'adwords',
      homepage: 'https://ads.google.com',
      dataFlow: DataFlow.out,
    },
    youtube: {
      id: IntegrationIDs.youtube,
      forceOrder: 0,
      imageSrc: 'https://cdn.zapier.com/storage/services/7633e61620664fb3a71911729ebce5ea.128x128.png',
      name: IntegrationNames.youtube,
      overview: 'Sync segments to Google Ad Audiences for retargeting on YouTube.',
      category: IntegrationCategory.advertising,
      description: 'YouTube Ads connect your message to the right audience.',
      parsedUseCases: [
        'Sync Customer.io segments to Google Ad Audiences, then retarget on YouTube',
        'Create Google Similar Audiences based on these Ad Audiences',
      ],
      enabled: false,
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      audienceSync: true,
      path: 'integrations-public.type',
      dynamicSegment: 'YouTube_Ads',
      homepage: 'https://youtube.com',
      dataFlow: DataFlow.out,
      hideFromFeatured: true,
    },
    googleAnalytics: {
      id: IntegrationIDs.googleAnalytics,
      forceOrder: 0,
      imageSrc: 'https://cdn.zapier.com/storage/services/8e760f76ff742d22fb037a3ed49e009d.128x128.png',
      name: IntegrationNames.googleAnalytics,
      overview: 'Send UTM Parameters along with your Customer.io messages.',
      category: IntegrationCategory.analytics,
      description: 'Google Analytics is an industry standard freemium web and mobile analytics package.',
      parsedUseCases: ['Automatically append UTM parameters to links in Customer.io campaigns'],
      enabled: false,
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      path: 'integrations-public.type',
      dynamicSegment: 'Google_Analytics',
      homepage: 'https://analytics.google.com',
      dataFlow: DataFlow.out,
    },
    facebook: {
      id: IntegrationIDs.facebook,
      forceOrder: 0,
      imageSrc: facebookImg,
      name: IntegrationNames.facebook,
      overview: 'Sync segments to Facebook Custom Audiences for retargeting on Facebook.',
      category: IntegrationCategory.advertising,
      description: 'Facebook Ads connect your message to the right audience.',
      parsedUseCases: [
        'Sync Customer.io segments to Facebook Custom Audiences',
        'Create Facebook Lookalike Audiences based on these Custom Audiences',
      ],
      enabled: false,
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      audienceSync: true,
      path: 'integrations-public.type',
      dynamicSegment: 'facebook',
      homepage: 'https://facebook.com',
      dataFlow: DataFlow.out,
    },
    instagram: {
      id: IntegrationIDs.instagram,
      forceOrder: 0,
      imageSrc: 'https://cdn.zapier.com/storage/developer/30129480e943e5b3e334394168ae16a2.128x128.png',
      name: IntegrationNames.instagram,
      overview: 'Sync segments to Facebook Custom Audiences for retargeting on Instagram.',
      category: IntegrationCategory.advertising,
      description: 'Instagram Ads connect your message to the right audience.',
      parsedUseCases: [
        'Sync Customer.io segments to Facebook Custom Audiences, then retarget on Instagram',
        'Create Facebook Lookalike Audiences based on these Custom Audiences',
      ],
      enabled: false,
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      audienceSync: true,
      path: 'integrations-public.type',
      dynamicSegment: 'Instagram',
      homepage: 'https://instagram.com',
      dataFlow: DataFlow.out,
      hideFromFeatured: true,
    },
    facebookLeadAds: {
      id: IntegrationIDs.facebookLeadAds,
      forceOrder: 0,
      imageSrc: facebookImg,
      name: IntegrationNames.facebookLeadAds,
      overview: 'Connect your Facebook Lead Ads forms to start messaging your leads',
      category: IntegrationCategory.leadGeneration,
      description:
        'Facebook Lead Ads makes signing up for business information easy for people and more valuable for businesses.',
      parsedUseCases: [
        'Add Customer.io profiles from Facebook Lead Ad submissions',
        'Trigger Customer.io campaigns from Facebook Lead Ad submissions',
      ],
      enabled: false,
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      path: 'integrations-public.type',
      dynamicSegment: 'Facebook_Lead_Ads',
      homepage: 'https://facebook.com',
      dataFlow: DataFlow.in,
      hideFromFeatured: true,
    },
    typeform: {
      id: IntegrationIDs.typeform,
      forceOrder: 0,
      imageSrc: typeformImg,
      name: IntegrationNames.typeform,
      overview: 'Create profiles and trigger campaigns from Typeform',
      category: IntegrationCategory.surveys,
      description: 'Typeform helps you run surveys, questionnaires, forms, contests and more across all devices.',
      parsedUseCases: ['Create Customer.io profiles and trigger campaigns when a new Typeform response is recorded.'],
      enabled: false,
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      path: 'integrations-public.type',
      dynamicSegment: 'Typeform',
      homepage: 'https://typeform.com',
      dataFlow: DataFlow.in,
      hideFromFeatured: true,
    },
    jotform: {
      id: IntegrationIDs.jotform,
      forceOrder: 0,
      imageSrc: jotformImg,
      name: IntegrationNames.jotform,
      overview: 'Create profiles and trigger campaigns from Jotform',
      category: IntegrationCategory.surveys,
      description:
        'Jotform lets you build forms fast, with powerful widgets and a responsive editor that works offline.',
      parsedUseCases: ['Create Customer.io profiles or events when a Jotform is submitted'],
      enabled: false,
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      path: 'integrations-public.type',
      dynamicSegment: 'Jotform',
      homepage: 'https://jotform.com',
      dataFlow: DataFlow.in,
      hideFromFeatured: true,
    },
    segmentOut: {
      id: IntegrationIDs.segmentOut,
      forceOrder: 0,
      imageSrc: segmentOutImg,
      name: IntegrationNames.segmentOut,
      subtitle: 'Source',
      overview: 'Send Customer.io message activity data such as opens, clicks, and subscribes to Segment.',
      category: IntegrationCategory.dataManagement,
      description: `Segment allows you to unify ${customerSingular} data and analytics in one place.`,
      parsedUseCases: [
        'Send message opens, clicks, unsubscribes, failures, bounces, and spam flags from Customer.io to Segment',
      ],
      enabled: false,
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      path: 'integrations-public.type',
      dynamicSegment: 'Segment_Source',
      homepage: 'https://segment.io',
      dataFlow: DataFlow.out,
    },
    line: {
      id: IntegrationIDs.line,
      forceOrder: 0,
      imageSrc: lineImg,
      name: IntegrationNames.line,
      overview: 'Send LINE messages in Customer.io.',
      description: `LINE is a messaging app that allows you to send and receive messages with your ${customersPlural}.`,
      parsedUseCases: ['Send LINE messages in Customer.io campaigns'],
      enabled: false,
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      path: 'integrations-public.type',
      dynamicSegment: 'LINE',
      homepage: 'https://line.me',
      dataFlow: DataFlow.channel,
    },
    twilio: {
      id: IntegrationIDs.twilio,
      forceOrder: 0,
      imageSrc: twilioImg,
      name: IntegrationNames.twilio,
      overview: 'Send SMS messages in Customer.io.',
      description: `Customer.io's SMS Action allows you to easily communicate with your ${customersPlural} through Customer.io.`,
      parsedUseCases: ['Send SMS messages in Customer.io campaigns'],
      enabled: false,
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      path: 'integrations-public.type',
      dynamicSegment: 'Twilio',
      homepage: 'https://twilio.com',
      dataFlow: DataFlow.channel,
    },
    WhatsApp: {
      id: IntegrationIDs.WhatsApp,
      forceOrder: 0,
      imageSrc: 'https://cdn3.iconfinder.com/data/icons/social-network-30/512/social-01-512.png',
      name: IntegrationNames.WhatsApp,
      overview: 'Send WhatsApp chats through Twilio',
      category: IntegrationCategory.dataManagement,
      description:
        'Once authorized with a WhatsApp business profile, Twilio provides the capability to send WhatsApp messages.',
      parsedUseCases: ['Send WhatsApp messages in Customer.io campaigns'],
      enabled: false,
      beta: false,
      type: IntegrationType.partnerBuilt,
      publicType: IntegrationType.existing,
      path: 'integrations.type',
      dynamicSegment: 'WhatsApp',
      homepage: 'https://www.twilio.com/whatsapp',
      dataFlow: DataFlow.channel,
    },
    slack: {
      id: IntegrationIDs.slack,
      forceOrder: 0,
      imageSrc: 'https://cdn.brandfetch.io/slack.com/lettermark/w/100/h/100',
      name: IntegrationNames.slack,
      overview: 'Send Slack messages in Customer.io.',
      description:
        "Customer.io's Slack Action helps your teams work better together by passing information directly into Slack triggered by user behavior in your app, all customized to your needs.",
      parsedUseCases: ['Send Slack messages to individuals or channels through Customer.io campaigns'],
      enabled: false,
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      path: 'integrations-public.type',
      dynamicSegment: 'Slack',
      homepage: 'https://slack.com',
      dataFlow: DataFlow.channel,
    },
    mparticleOut: {
      id: IntegrationIDs.mparticleOut,
      forceOrder: 0,
      imageSrc: 'https://cdn.brandfetch.io/mparticle.com/lettermark/w/100/h/100',
      name: IntegrationNames.mparticleOut,
      subtitle: 'Source',
      overview: 'Send data from Customer.io to mParticle.',
      category: IntegrationCategory.dataManagement,
      description:
        'mParticle is a customer data platform, allowing marketers to integrate and orchestrate their entire growth stack so they win in key moments of the customer journey.',
      parsedUseCases: [
        'Sync mParticle Audiences to Customer.io People and Attributes',
        'Forward Events from mParticle to Customer.io',
        'Documentation for mParticle Audiences: http://docs.mparticle.com/integrations/customerio/audience',
        'Documentation for mParticle Events: http://docs.mparticle.com/integrations/customerio/event]',
      ],
      enabled: false,
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      path: 'integrations-public.type',
      dynamicSegment: 'Mparticle_Source',
      homepage: 'https://mparticle.com',
      dataFlow: DataFlow.out,
    },
    cdpOut: {
      id: IntegrationIDs.cdpOut,
      forceOrder: 0,
      imageSrc: cdpOutImg,
      name: IntegrationNames.cdpOut,
      overview: 'Send message activity to the Customer.io Data Pipelines',
      category: IntegrationCategory.dataManagement,
      description: `Our CDP allows you to unify ${customerSingular} data and analytics in one place.`,
      parsedUseCases: [
        'Send message opens, clicks, unsubscribes, failures, bounces, and spam flags from Customer.io to our CDP',
      ],
      enabled: false,
      beta: false,
      type: IntegrationType.native,
      publicType: IntegrationType.existing,
      path: 'integrations-public.type',
      dynamicSegment: 'cdp_out',
      homepage: 'https://customer.io',
      dataFlow: DataFlow.out,
    },
  });
}
